import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { categoryBySlug, SECONDHAND_SECTIONS, SECONDHAND_MARKETS } from "@/lib/lifeCategories";
import { money } from "@/lib/pathways";
import { track } from "@/lib/api";
import { EmailCapture } from "@/components/EmailCapture";
import { CheaperWayCTA, ComingSoon } from "@/components/CheaperWayCTA";
import { ArrowUpRight, ArrowRight } from "lucide-react";

const STRATEGY_SLUGS = {
  free: { type: "FREE IT", name: "FREE IT", tagline: "Get it without buying it." },
  refurbished: { type: "REFURBISH IT", name: "REFURBISHED", tagline: "Certified, tested, and warrantied — for a lot less than new." },
  borrow: { type: "BORROW IT", name: "BORROW IT", tagline: "Borrow it instead of owning it." },
  rent: { type: "RENT IT", name: "RENT IT", tagline: "Rent it — especially for one-time occasions. Access, not ownership." },
  deals: { type: "DEAL IT", name: "DEALS", tagline: "Real sales and offers worth knowing." },
};

export default function CategoryPage() {
  const { slug } = useParams();
  const nav = useNavigate();
  const cat = categoryBySlug(slug);
  const strat = STRATEGY_SLUGS[slug];
  const [sources, setSources] = useState([]);
  const [opps, setOpps] = useState([]);
  const [q, setQ] = useState("");

  const isSecondhand = cat?.special === "secondhand";
  const isClaims = slug === "claims";

  useEffect(() => {
    let params = {};
    if (strat) params = { strategy: strat.type };
    else if (isSecondhand) params = { strategy: "SECONDHAND IT" };
    else if (isClaims) params = { strategy: "CLAIM IT" };
    else if (cat) params = { category: cat.oppCategory };
    api.get("/sources", { params }).then((r) => setSources(r.data.sources || [])).catch(() => {});
    api.get("/opportunities", { params }).then((r) => setOpps(r.data.opportunities || [])).catch(() => {});
  }, [slug]); // eslint-disable-line

  if (!cat && !strat) return <div className="max-w-7xl mx-auto px-6 py-24 font-display text-3xl">Not found. <Link to="/" className="text-[#EF3340]">Home</Link></div>;

  const title = cat ? cat.name : strat.name;
  const tagline = cat ? cat.tagline : strat.tagline;
  const isEmpty = sources.length === 0 && opps.length === 0;

  const runSearch = (e) => {
    e.preventDefault();
    if (q.trim()) nav(`/find?q=${encodeURIComponent(q)}`);
  };

  return (
    <div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <Link to="/" className="font-label text-[10px] tracking-widest text-stone-500">LIFE / {title}</Link>
        <h1 className="font-display text-4xl md:text-6xl leading-[0.95] mt-3 mb-3">{title}</h1>
        <p className="font-serif text-xl text-stone-300 max-w-2xl mb-8">{tagline}</p>

        <form onSubmit={runSearch} className="border hairline bg-[#141217] flex flex-col md:flex-row mb-10">
          <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="category-search-input"
            placeholder={`What are you about to spend money on in ${title.split(" ")[0].toLowerCase()}?`}
            className="flex-1 bg-transparent px-6 py-5 text-lg font-serif text-stone-100 placeholder:text-stone-600 outline-none" />
          <button type="submit" data-testid="category-search-btn" className="font-label text-xs tracking-widest bg-[#EF3340] text-white px-8 py-5 hover:bg-[#cc2a35] transition-colors whitespace-nowrap">RUN THE AUDIT →</button>
        </form>

        {isSecondhand && (
          <div className="mb-12">
            <div className="eyebrow mb-4">BROWSE BY WHAT YOU NEED</div>
            <div className="flex flex-wrap gap-2">
              {SECONDHAND_SECTIONS.map((s) => (
                <button key={s.name} onClick={() => nav(`/find?q=${encodeURIComponent(s.q)}`)} data-testid={`sh-section-${s.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className="font-label text-xs border hairline px-3 py-2 text-stone-400 hover:border-[#EF3340] hover:text-[#EF3340] transition-colors">{s.name}</button>
              ))}
            </div>
          </div>
        )}

        {cat && !isSecondhand && (
          <div className="mb-12">
            <div className="eyebrow mb-4">IN THIS CATEGORY</div>
            <div className="flex flex-wrap gap-2">
              {cat.subs.map((s) => (
                <button key={s} onClick={() => nav(`/find?q=${encodeURIComponent(s)}`)}
                  className="font-label text-xs border hairline px-3 py-2 text-stone-400 hover:border-[#EF3340] hover:text-[#EF3340] transition-colors">{s}</button>
              ))}
            </div>
          </div>
        )}

        {opps.length > 0 && (
          <div className="mb-12">
            <h2 className="font-label text-sm tracking-widest text-[#EF3340] mb-5">LIVE OPPORTUNITIES</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {opps.map((o, i) => (
                <a key={o.id} href={o.affiliate_url || o.destination_url || o.official_url || "#"} target="_blank" rel="noopener noreferrer"
                  onClick={() => track("opportunity_viewed", { opp: o.title })} data-testid={`cat-opp-${i}`}
                  className="group border hairline bg-[#141217] p-6 flex flex-col hover:border-[#EF3340] transition-colors">
                  <div className="flex items-center justify-between mb-3">
                    <span className="font-label text-[10px] tracking-widest text-[#EF3340] border border-[#EF3340]/40 px-2 py-1">{o.type}</span>
                    {o.savings > 0 && <span className="font-display text-lg text-[#5FB878]">save {money(o.savings)}</span>}
                  </div>
                  <h3 className="font-display text-xl leading-tight mb-2 group-hover:text-[#EF3340] transition-colors">{o.title}</h3>
                  <p className="font-serif text-sm text-stone-400 mb-3">{o.description}</p>
                  {o.tradeoffs && <p className="font-label text-[10px] text-stone-500 mb-3">TRADEOFF · {o.tradeoffs}</p>}
                  <span className="mt-auto font-label text-[10px] text-stone-500">{o.source}</span>
                </a>
              ))}
            </div>
          </div>
        )}

        <div>
          <h2 className="font-label text-sm tracking-widest text-[#EF3340] mb-2">{isSecondhand ? "WHERE TO LOOK" : "SOURCES WORTH KNOWING"}</h2>
          <p className="font-serif text-sm text-stone-500 mb-5">Sources, not endorsements. We recommend the best option even when it doesn't pay us.</p>
          {isEmpty ? (
            <ComingSoon category={title} />
          ) : (
          <div className="border-l border-t hairline grid sm:grid-cols-2 lg:grid-cols-3">
            {sources.map((s, i) => (
              <a key={s.id} href={s.affiliate_url || s.destination_url || s.official_url} target="_blank" rel="noopener noreferrer" onClick={() => track("external_clicks", { source: s.name, affiliate: !!s.is_affiliate })}
                data-testid={`source-${i}`} className="group border-r border-b hairline p-5 hover:bg-[#141217] transition-colors">
                <div className="flex items-start justify-between gap-2">
                  <span className="font-display text-lg leading-tight group-hover:text-[#EF3340] transition-colors">{s.name}</span>
                  <ArrowUpRight className="w-4 h-4 text-stone-600 group-hover:text-[#EF3340] shrink-0" />
                </div>
                <div className="flex flex-wrap gap-1 mt-2">
                  {(s.strategies || []).slice(0, 3).map((st) => (
                    <span key={st} className="font-label text-[9px] tracking-widest text-stone-500 border hairline px-1.5 py-0.5">{st}</span>
                  ))}
                </div>
                <div className="font-label text-[10px] text-stone-600 mt-2">{s.national_or_local === "local" ? "LOCAL" : "NATIONAL"} · {s.subcategory}</div>
              </a>
            ))}
            {sources.length === 0 && <div className="border-r border-b hairline p-5 font-label text-xs text-stone-500">No sources catalogued here yet.</div>}
          </div>
          )}
        </div>

        <div className="mt-14">
          <CheaperWayCTA category={title} />
        </div>

        {isSecondhand && (
          <div className="mt-12 border-2 border-[#EF3340] bg-[#141217] p-8">
            <div className="eyebrow mb-3">BEFORE YOU BUY SECONDHAND</div>
            <p className="font-serif text-lg text-stone-200 max-w-3xl">
              Compare the <span className="text-[#F4EEE5]">new price</span> against the secondhand price, but also weigh
              condition, buyer protection, return policy, warranty, shipping vs. local pickup, and seller quality.
              Inspect for damage, smoke and pests before paying. Cheaper isn't the goal — the right call is.
            </p>
          </div>
        )}
      </div>
      <EmailCapture />
    </div>
  );
}
